import { useQuery } from '@apollo/client';
import { styled } from '@mui/material/styles';
import { LoaderSkeleton, HrefLink } from '@bloomays-lib/ui.shared';
import { GET_JOB_FUNNEL_PROJECTS } from '@bloomays-lib/adapter.api-talent';
import { getApolloErrorCode, errorLogger } from '../../helpers/error';
import { notify } from '../../helpers/toastify';
import { useTranslation } from 'react-i18next';
import { ApolloGqlJobFunnel } from '@bloomays-lib/types.shared';
import DataGrid from '../molecules/DataGrid';
import { GridColDef, GridValueGetter } from '@mui/x-data-grid-premium';
import { format } from 'date-fns/fp/format';
import Typography from '@mui/material/Typography';
import VisibilityIcon from '@mui/icons-material/Visibility';

const JobFunnelProjectDashboard = () => {
  const { loading, error, data } = useQuery<{ getJobFunnelProjects: ApolloGqlJobFunnel[] }>(GET_JOB_FUNNEL_PROJECTS, {
    context: { clientName: 'api.talents' },
  });
  const { t } = useTranslation(['jobs']);

  const columns: GridColDef[] = [
    {
      field: 'actions',
      headerName: '',
      width: 50,
      filterable: false,
      renderCell: (params) => (
        <HrefLink href={`/jobs/${params.row.id}`}>
          <VisibilityIcon />
        </HrefLink>
      ),
    },
    { field: 'name', headerName: t('projectName'), width: 200 },
    { field: 'jobTitle', headerName: t('jobTitle'), width: 200 },
    {
      field: 'society',
      headerName: t('societyName'),
      width: 200,
      valueGetter: (value: GridValueGetter<ApolloGqlJobFunnel>, row: ApolloGqlJobFunnel) => row.society.name,
    },
    { field: 'dealType', headerName: t('dealType'), width: 150 },
    { field: 'jobType', headerName: t('jobType'), width: 200 },
    {
      field: 'status',
      headerName: t('status'),
      width: 150,
    },
    {
      field: 'winDate',
      headerName: t('winDate'),
      type: 'date',
      width: 200,
      valueFormatter: (value: Date) => {
        if (!value) return '';
        return format('dd/MM/yyyy', value);
      },
      valueGetter: (value: GridValueGetter<ApolloGqlJobFunnel>, row: ApolloGqlJobFunnel) =>
        row.winDate ? new Date(row.winDate) : undefined,
    },
    {
      field: 'createdAt',
      headerName: t('createdAt'),
      type: 'date',
      width: 200,
      valueFormatter: (value: string) => {
        if (!value) return '';
        return format('dd/MM/yyyy', new Date(value));
      },
      valueGetter: (value: GridValueGetter<ApolloGqlJobFunnel>, row: ApolloGqlJobFunnel) =>
        row.createdAt ? new Date(row.createdAt) : undefined,
    },
    {
      field: 'recruiter',
      headerName: t('accountManager'),
      width: 200,
      valueGetter: (value: GridValueGetter<ApolloGqlJobFunnel>, row: ApolloGqlJobFunnel) => row.recruiter?.email,
    },
  ];

  if (loading) {
    return <LoaderSkeleton label={t('loadingText', { ns: 'random' })} height={600} width={800} />;
  }

  if (error) {
    const errorCode = getApolloErrorCode(error);

    switch (errorCode) {
      case 'OBJECT_NOT_FOUND':
        notify('error', t('jobFunnelProjectNotFound'));
        break;
      default:
        errorLogger(error, {
          extraInfos: `Internal server error, impossible to retrieve data about job funnel project`,
        });
        notify('error', t('randomLKError'), error);
    }
    return <div>Une erreur est survenue lors du chargement du projet</div>;
  }

  return (
    <Container>
      <Typography variant="heading4" sx={{ mb: 3 }}>
        Liste de Jobs ouverts
      </Typography>
      <DataGrid
        rows={data?.getJobFunnelProjects || []}
        columns={columns}
        hideFooterPagination={false}
        getRowId={(row: ApolloGqlJobFunnel) => row.id}
        getRowClassName={() => 'cellStyle'}
        uniqueDatagridId="job-funnel-projects"
        pagination
        pageSize={10}
      />
    </Container>
  );
};

const Container = styled('div')(
  ({ theme }) => `
  background-color: ${theme.palette.neutral[10]};
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 90px;
  border-radius: 10px;
  width: 100%;
  height: 100%;
`,
);

export default JobFunnelProjectDashboard;
